import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";
import { notificationSound } from "@/lib/notification-sound";
import { safeRemoveChannel } from "@/lib/supabase-channel";

export interface SessionWithWaiter {
  id: string;
  user_id: string;
  status: "pending" | "active" | "expired" | "revoked";
  requested_at: string;
  started_at: string | null;
  expires_at: string | null;
  checkin_method: "code" | "approval";
  device_info: Record<string, unknown> | null;
  waiter_name: string | null;
  waiter_email: string | null;
}

interface UseWaiterSessionsManagementReturn {
  activeSessions: SessionWithWaiter[];
  activeWaiterSessions: SessionWithWaiter[];
  activeTabletSessions: SessionWithWaiter[];
  pendingRequests: SessionWithWaiter[];
  recentSessions: SessionWithWaiter[];
  isLoading: boolean;
  approveSession: (sessionId: string) => Promise<boolean>;
  rejectSession: (sessionId: string) => Promise<boolean>;
  revokeSession: (sessionId: string) => Promise<boolean>;
  refetch: () => Promise<void>;
}

export function useWaiterSessionsManagement(): UseWaiterSessionsManagementReturn {
  const { activeRestaurant } = useRestaurantContext();
  const [sessions, setSessions] = useState<SessionWithWaiter[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const previousPendingCountRef = useRef<number>(0);

  const fetchSessions = useCallback(async () => {
    if (!activeRestaurant) {
      setSessions([]);
      setIsLoading(false);
      return;
    }

    try {
      // Buscar sessões das últimas 24h
      const yesterday = new Date();
      yesterday.setHours(yesterday.getHours() - 24);

      const { data: sessionsData, error: sessionsError } = await supabase
        .from("waiter_work_sessions")
        .select("*")
        .eq("restaurant_id", activeRestaurant.id)
        .gte("created_at", yesterday.toISOString())
        .order("created_at", { ascending: false });

      if (sessionsError) throw sessionsError;

      if (!sessionsData || sessionsData.length === 0) {
        setSessions([]);
        setIsLoading(false);
        return;
      }

      // Buscar dados dos usuários
      const userIds = [...new Set(sessionsData.map((s) => s.user_id))];
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select("id, full_name")
        .in("id", userIds);

      if (profilesError) {
        console.error("Erro ao buscar perfis:", profilesError);
      }

      // Combinar dados
      const profilesMap = new Map(
        (profilesData || []).map((p) => [p.id, p.full_name])
      );

      const sessionsWithWaiter: SessionWithWaiter[] = sessionsData.map((s) => ({
        id: s.id,
        user_id: s.user_id,
        status: s.status as SessionWithWaiter["status"],
        requested_at: s.requested_at,
        started_at: s.started_at,
        expires_at: s.expires_at,
        checkin_method: s.checkin_method as SessionWithWaiter["checkin_method"],
        device_info: s.device_info as Record<string, unknown> | null,
        waiter_name: profilesMap.get(s.user_id) || null,
        waiter_email: null, // Não expor email por segurança
      }));

      setSessions(sessionsWithWaiter);
    } catch (error) {
      console.error("Erro ao buscar sessões:", error);
      setSessions([]);
    } finally {
      setIsLoading(false);
    }
  }, [activeRestaurant]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Escutar mudanças em tempo real
  useEffect(() => {
    if (!activeRestaurant) return;

    const channel = supabase
      .channel(`waiter-sessions-management-${activeRestaurant.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "waiter_work_sessions",
          filter: `restaurant_id=eq.${activeRestaurant.id}`,
        },
        (payload) => {
          if (payload.eventType === "INSERT") {
            const newSession = payload.new as { status: string };
            if (newSession.status === "pending") {
              // Play alert sound and show longer toast
              notificationSound.playNotification("alert");
              toast.info("Nova solicitação de check-in de garçom!", {
                duration: 15000,
                description: "Clique em 'Garçons' para aprovar.",
              });
            }
          }
          fetchSessions();
        }
      )
      .subscribe();

    return () => {
      safeRemoveChannel(channel);
    };
  }, [activeRestaurant, fetchSessions]);

  const activeSessions = sessions.filter((s) => {
    if (s.status !== "active") return false;
    if (!s.expires_at) return true;
    return new Date(s.expires_at) > new Date();
  });

  // Separate active sessions by type
  const activeWaiterSessions = activeSessions.filter((s) => {
    const deviceInfo = s.device_info as { sessionType?: string } | null;
    return deviceInfo?.sessionType !== "tablet";
  });

  const activeTabletSessions = activeSessions.filter((s) => {
    const deviceInfo = s.device_info as { sessionType?: string } | null;
    return deviceInfo?.sessionType === "tablet";
  });

  const pendingRequests = sessions.filter((s) => s.status === "pending");

  // Play sound when pending requests increase (new request detected after initial load)
  useEffect(() => {
    if (pendingRequests.length > previousPendingCountRef.current && previousPendingCountRef.current > 0) {
      notificationSound.playNotification("alert");
    }
    previousPendingCountRef.current = pendingRequests.length;
  }, [pendingRequests.length]);

  const recentSessions = sessions.filter(
    (s) => s.status === "expired" || s.status === "revoked"
  );

  const approveSession = async (sessionId: string): Promise<boolean> => {
    try {
      const { error } = await supabase.rpc("approve_waiter_session", {
        p_session_id: sessionId,
      });

      if (error) throw error;

      toast.success("Sessão aprovada!");
      await fetchSessions();
      return true;
    } catch (error) {
      console.error("Erro ao aprovar sessão:", error);
      toast.error("Erro ao aprovar sessão");
      return false;
    }
  };

  const rejectSession = async (sessionId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("waiter_work_sessions")
        .update({ status: "revoked", revoked_at: new Date().toISOString() })
        .eq("id", sessionId);

      if (error) throw error;

      toast.success("Solicitação recusada");
      await fetchSessions();
      return true;
    } catch (error) {
      console.error("Erro ao rejeitar sessão:", error);
      toast.error("Erro ao recusar solicitação");
      return false;
    }
  };

  const revokeSession = async (sessionId: string): Promise<boolean> => {
    try {
      const { error } = await supabase.rpc("revoke_waiter_session", {
        p_session_id: sessionId,
      });

      if (error) throw error;

      toast.success("Sessão revogada");
      await fetchSessions();
      return true;
    } catch (error) {
      console.error("Erro ao revogar sessão:", error);
      toast.error("Erro ao revogar sessão");
      return false;
    }
  };

  return {
    activeSessions,
    activeWaiterSessions,
    activeTabletSessions,
    pendingRequests,
    recentSessions,
    isLoading,
    approveSession,
    rejectSession,
    revokeSession,
    refetch: fetchSessions,
  };
}
